import React, { useState, useEffect } from "react";
import { X, RefreshCw, Copy, Check, BookOpen, FileText, Building2, Phone } from "lucide-react";
import webAuthService from "../services/webAuthService";
import { apiClient } from "../services/api";

const MeetingIdModal = ({ isOpen, onClose, onConfirm }) => {
  const [meetingId, setMeetingId] = useState("");
  const [prospectName, setProspectName] = useState("");
  const [copied, setCopied] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [userId, setUserId] = useState(null);
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState([]);
  const [selectedKBFiles, setSelectedKBFiles] = useState([]);
  const [loadingKB, setLoadingKB] = useState(false);
  const [isColdCall, setIsColdCall] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMeetingId("");
      setProspectName("");
      setCopied(false);
      setSelectedGroupId("");
      setSelectedKBFiles([]);
      setIsColdCall(false);
      fetchUserAndGroups();
    }
  }, [isOpen]);

  const fetchUserAndGroups = async () => {
    try {
      const uid = await webAuthService.getUserId();
      if (uid) {
        setUserId(uid);
        const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
        const response = await fetch(`${baseURL}/groups?user_id=${uid}`);
        const data = await response.json();
        if (data.status === "success") {
          setGroups(data.groups || []);
        }
        fetchKnowledgeBaseFiles(uid);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  const fetchKnowledgeBaseFiles = async (uid) => {
    try {
      setLoadingKB(true);
      const response = await apiClient.get(`/api/knowledge-base/user/${uid}`);
      setKnowledgeBaseFiles(response.data.files || []);
    } catch (error) {
      console.error("Error fetching knowledge base files:", error);
      setKnowledgeBaseFiles([]);
    } finally {
      setLoadingKB(false);
    }
  };

  const toggleKBFile = (fileId) => {
    setSelectedKBFiles((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  const generateRandomMeetingId = () => {
    const randomPart = Math.random().toString(36).substring(2, 15);
    setMeetingId(`custom_${randomPart}`);
  };

  // Meeting IDs end up in URLs, WS query params, and DB keys — keep them
  // strictly [A-Za-z0-9_-]. Filter at input time so the user only ever sees a
  // valid value; spaces become `-` for convenience.
  const MEETING_ID_MAX_LEN = 64;
  const sanitizeMeetingId = (value) =>
    value
      .replace(/\s+/g, "-")
      .replace(/[^A-Za-z0-9_-]/g, "")
      .slice(0, MEETING_ID_MAX_LEN);

  const handleConfirm = () => {
    if (meetingId) {
      onConfirm(meetingId, selectedGroupId, selectedKBFiles, prospectName.trim(), isColdCall);
      onClose();
    }
  };

  const handleCopy = async () => {
    if (!meetingId) return;
    try {
      await navigator.clipboard.writeText(meetingId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy meeting ID:", error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleConfirm();
    }
  };

  if (!isOpen) return null;

  const allSelected =
    knowledgeBaseFiles.length > 0 &&
    selectedKBFiles.length === knowledgeBaseFiles.length;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span aria-hidden="true">📝</span>
            Enter Meeting ID
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body — only this scrolls */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
          <p className="text-sm text-gray-600 leading-relaxed">
            Enter a meeting ID from your video conference (Teams, Google Meet,
            etc.) or generate a random one to start your capture.
          </p>

          {/* Meeting ID */}
          <div>
            <label
              htmlFor="meeting-id"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Meeting ID
            </label>
            <div className="flex gap-2">
              <input
                id="meeting-id"
                type="text"
                value={meetingId}
                onChange={(e) => setMeetingId(sanitizeMeetingId(e.target.value))}
                onKeyDown={handleKeyDown}
                placeholder="e.g., abc-defg-hij"
                autoFocus
                maxLength={MEETING_ID_MAX_LEN}
                autoComplete="off"
                spellCheck={false}
                className="flex-1 min-w-0 px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
              />
              {meetingId && (
                <button
                  type="button"
                  onClick={handleCopy}
                  className={`p-2.5 rounded-lg border transition-colors ${
                    copied
                      ? "bg-green-500 border-green-500 text-white"
                      : "border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                  title="Copy meeting ID"
                  aria-label="Copy meeting ID"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              )}
              <button
                type="button"
                onClick={generateRandomMeetingId}
                className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap"
                title="Generate random meeting ID"
              >
                <RefreshCw size={14} />
                Random
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              Letters, numbers, <span className="font-mono">_</span> and{" "}
              <span className="font-mono">-</span> only. Spaces become{" "}
              <span className="font-mono">-</span>. Max {MEETING_ID_MAX_LEN}{" "}
              characters.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              From <span className="font-mono">meet.google.com/abc-defg-hij</span>{" "}
              use just <span className="font-mono">abc-defg-hij</span>.
            </p>
          </div>

          {/* Cold Call toggle */}
          <div>
            <label
              htmlFor="cold-call"
              className={`flex items-start gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-colors ${
                isColdCall
                  ? "border-orange-400 bg-orange-50"
                  : "border-gray-200 bg-gray-50 hover:border-gray-300"
              }`}
            >
              <input
                id="cold-call"
                type="checkbox"
                checked={isColdCall}
                onChange={(e) => setIsColdCall(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded accent-orange-500 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <Phone
                    size={14}
                    className={isColdCall ? "text-orange-500" : "text-gray-500"}
                  />
                  <span className="text-sm font-medium text-gray-900">
                    Cold Call mode
                  </span>
                  {isColdCall && (
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">
                      On
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  AI suggestions trigger only when the client asks a question.
                  Uses focused context for the fastest possible response.
                </p>
              </div>
            </label>
          </div>

          {/* Prospect / Account name */}
          <div>
            <label
              htmlFor="prospect-name"
              className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
            >
              <Building2 size={14} className="text-gray-600" />
              Prospect / Account name{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="prospect-name"
              type="text"
              value={prospectName}
              onChange={(e) => setProspectName(e.target.value)}
              placeholder="e.g., Acme Corporation"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              We'll research this company from public sources and show Customer
              AI Insights during your meeting.
            </p>
          </div>

          {/* Group */}
          <div>
            <label
              htmlFor="group-select"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Group{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              id="group-select"
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
            >
              <option value="">No group</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1.5">
              Organize this meeting under a client or project group.
            </p>
          </div>

          {/* Knowledge Base */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <BookOpen size={14} className="text-gray-600" />
                Knowledge Base{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              {knowledgeBaseFiles.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    if (allSelected) {
                      setSelectedKBFiles([]);
                    } else {
                      setSelectedKBFiles(knowledgeBaseFiles.map((f) => f.id));
                    }
                  }}
                  className="text-xs font-medium text-red-500 hover:text-red-600"
                >
                  {allSelected ? "Deselect all" : "Select all"}
                </button>
              )}
            </div>
            {loadingKB ? (
              <div className="text-center py-5 text-sm text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
                Loading knowledge base files…
              </div>
            ) : knowledgeBaseFiles.length === 0 ? (
              <div className="text-center py-5 text-sm text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
                No knowledge base files. Upload some from the Knowledge Base
                tab.
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg bg-white divide-y divide-gray-100 overflow-hidden">
                {knowledgeBaseFiles.map((file) => {
                  const isSelected = selectedKBFiles.includes(file.id);
                  return (
                    <label
                      key={file.id}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                        isSelected ? "bg-red-50" : "hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleKBFile(file.id)}
                        className="w-4 h-4 rounded accent-red-500 flex-shrink-0"
                      />
                      <FileText
                        size={14}
                        className="text-red-500 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-sm font-medium text-gray-900 truncate"
                          title={file.file_name}
                        >
                          {file.file_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {file.chunks_count || 0} chunks
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            {knowledgeBaseFiles.length > 0 && (
              <p className="text-xs text-gray-500 mt-1.5">
                {selectedKBFiles.length} selected — these provide context to
                the AI during the meeting.
              </p>
            )}
          </div>
        </div>

        {/* Footer — always visible */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!meetingId}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Start Capture
          </button>
        </div>
      </div>
    </div>
  );
};

export default MeetingIdModal;
